import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { TToken } from "../../models";
import {
  FormValues,
  TCheckTokenToResetFormValues,
  TForgotPassFormValues,
  TUpdatePasswordFormValues,
} from "../../models/form";
import ImpersonationService from "../../services/ImpersonationService";
import { appLogOutAction, setProfilePictureAction } from "../auth/slice";
import { getProfileDetailsThunk, startReferroChecksThunk } from "../onboarding/thunk";
import { resetSelectedCentreDetails } from "../vacancy/slice";
import { setTokenAction } from "./slice";
import {
  TCheckEmailVerifyResponse,
  TEmailValidationResponse,
  TForgotPasswordResponse,
  TGetAddressSuggestionsResponse,
  TGetJobDetailsBasedOnAddressResponse,
  TMeResponse,
  TNearbySuburb,
  TNearbySuburbsApiResponse,
  TNearbySuburbsParams,
  TProfileImageResponse,
  TProfilePicValues,
  TRefreshRepsonse,
  TResendInvitationResponse,
  TSignInResponse,
  TSignUpResponse,
  TUpdatePasswordResponse,
} from "./types";

/**
 * POST method
 * signUpThunk - Submit the Sign Up form
 */
export const signUpThunk = createAsyncThunk(
  "auth/signUpThunk",
  async (data: FormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseSignUp: AxiosResponse<TSignUpResponse> = await axios.post(
        `/v1/auth/register`,
        data
      );
      return responseSignUp.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * POST method
 * checkEmailValidationThunk - Check th email is already exists or not.
 */
export const checkEmailValidationThunk = createAsyncThunk(
  "auth/checkEmailValidationThunk",
  async (data: FormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseEmailValidation: AxiosResponse<TEmailValidationResponse> =
        await axios.post(`/v1/auth/email-validate`, data);
      return responseEmailValidation.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * POST method
 * signInThunk - Submit the sign In form
 */
export const signInThunk = createAsyncThunk(
  "auth/signInThunk",
  async (data: FormValues, { rejectWithValue, dispatch }) => {
    try {
      const responseSignIn: AxiosResponse<TSignInResponse> = await axios.post(
        `/v1/auth/login`,
        data
      );
      await dispatch(setTokenAction(responseSignIn.data.data.token));
      if (responseSignIn.data.data.user.role.id === 5) {
        if(responseSignIn.data.data.referoo_status === "pending" || responseSignIn.data.data.referoo_status === "in_progress"){
          await dispatch(startReferroChecksThunk({ isReopen: false, hasPoliceCheck: false }));
        }
        await dispatch(getProfileDetailsThunk());
      }
      return responseSignIn.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data);
    }
  }
);

/**
 * GET method
 * getMeThunk - Get the loggedIn user details
 */
export const getMeThunk = createAsyncThunk(
  "auth/getMeThunk",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const responseUserData: AxiosResponse<TMeResponse> = await axios.get(
        `/v1/auth/me`
      );
      // Don't overwrite the token during impersonation - we need to keep the impersonation token
      if (!ImpersonationService.isImpersonating()) {
        await dispatch(setTokenAction(responseUserData.data.data.token));
      }
      if (responseUserData.data.data.user.role.id === 5) {  
        // if(responseUserData.data.data.user.referoo_status === "pending" || responseUserData.data.data.user.referoo_status === "in_progress"){
        //   await dispatch(startReferroChecksThunk());
        // }      
        await dispatch(getProfileDetailsThunk());        
        // await dispatch(getCandidatePendingTimesheetThunk())
      }

      return responseUserData.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data);
    }
  }
);

/**
 * POST method
 * logOutThunk - log Out from the app
 */
export const logOutThunk = createAsyncThunk(
  "auth/logOutThunk",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TSignInResponse> = await axios.post(
        `/v1/auth/logout`
      );
      await dispatch(resetSelectedCentreDetails());
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data);
    } finally {
      dispatch(appLogOutAction());
    }
  }
);

/**
 * POST method
 * refreshTokenThunk - Refresh the taoken for every requests
 */
export const refreshTokenThunk = createAsyncThunk(
  "auth/refreshTokenThunk",
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const responseRefreshToken: AxiosResponse<TRefreshRepsonse> =
        await axios.post(`/auth/refresh`);
      // dispatch(setTokenAction(responseRefreshToken.data.data))
      return responseRefreshToken.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data);
    }
  }
);

/**
 * POST method
 * forgotPasswordThunk - Forgot Password submit
 */
export const forgotPasswordThunk = createAsyncThunk(
  "auth/forgotPasswordThunk",
  async (data: TForgotPassFormValues, { rejectWithValue }) => {
    try {
      const response: AxiosResponse<TForgotPasswordResponse> = await axios.post(
        `/v1/auth/forgot-password`,
        data
      );
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * POST method
 * checkTokenToUpdatePasswordThunk - Check the token to reset the password
 */
export const checkTokenToUpdatePasswordThunk = createAsyncThunk(
  "auth/checkTokenToUpdatePasswordThunk",
  async (data: TCheckTokenToResetFormValues, { rejectWithValue }) => {
    try {
      await axios.post(`/v1/auth/validate-token`, data);
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * POST method
 * updatePasswordThunk - Update password API
 */
export const updatePasswordThunk = createAsyncThunk(
  "auth/updatePasswordThunk",
  async (data: TUpdatePasswordFormValues, { rejectWithValue }) => {
    try {
      const response: AxiosResponse<TUpdatePasswordResponse> = await axios.post(
        `/v1/auth/reset-password`,
        data
      );
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * POST method
 * resendInvitationThunk - Resend the invitation
 */
export const resendInvitationThunk = createAsyncThunk(
  "auth/resendInvitationThunk",
  async (data: FormValues, { dispatch, rejectWithValue }) => {
    try {
      const responseResendInvitation: AxiosResponse<TResendInvitationResponse> =
        await axios.post(`/v1/auth/resend-invitation`, data);
      return responseResendInvitation.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data);
    }
  }
);

/**
 * POST method
 * uploadsProfilePictureThunk - Upload the profile picture
 */
export const uploadsProfilePictureThunk = createAsyncThunk(
  "app/uploadsProfilePictureThunk",
  async (data: FormData | TProfilePicValues, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TProfileImageResponse> = await axios.post(
        `/v1/uploads/profile-image`,
        data
      );
      await dispatch(setTokenAction(response.data.data.token));
      await dispatch(
        setProfilePictureAction(response.data.data.profile_picture)
      );
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * POST method
 * checkEmailVerifyThunk - Check and verify the email address
 */
export const checkEmailVerifyThunk = createAsyncThunk(
  "app/checkEmailVerifyThunk",
  async (data: FormValues, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TCheckEmailVerifyResponse> =
        await axios.post(`/v1/auth/email-verification`, data);
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * GET method
 * getAddressSuggestionsThunk - Get sddress suggestions from google
 */
export const getAddressSuggestionsThunk = createAsyncThunk<
  TGetAddressSuggestionsResponse,
  string
>(
  "auth/getAddressSuggestionsThunk",
  async (data, { rejectWithValue }) => {
    try {
      const responseAddressSuggestions: AxiosResponse<TGetAddressSuggestionsResponse> = await axios.get(
        `/v1/addresses-list?address=${data}`
      );
      return responseAddressSuggestions.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data);
    }
  }
);

/**
 * GET method
 * getNearbySuburbsThunk - Get the Near by Suburbs based on address and radius
 */
export const getNearbySuburbsThunk = createAsyncThunk(
  "auth/getNearbySuburbsThunk",
  async (params: TNearbySuburbsParams, { dispatch, rejectWithValue }) => {
    try {
      const { address, radius, search } = params;
      const encodedAddress = encodeURIComponent(address);
      const encodedSearch = search
        ? `&search=${encodeURIComponent(search)}`
        : "";

      const response: AxiosResponse<TNearbySuburbsApiResponse> =
        await axios.get(
          `/v1/candidates/nearby-suburbs?address=${encodedAddress}&radius=${radius}${encodedSearch}`
        );

      const rawData = response.data;

      const suburbs: TNearbySuburb[] = [];
      let token: TToken | undefined;

      if (rawData.data && typeof rawData.data === "object") {
        Object.entries(rawData.data).forEach(([key, value]) => {
          if (key === "token") {
            token = value as TToken;
            dispatch(setTokenAction(token));
          } else if (
            typeof value === "object" &&
            "suburb" in value &&
            "postcode" in value
          ) {
            suburbs.push({
              id: Number(key),
              ...value,
              label: `${value.suburb}`,
            });
          }
        });
      }

      const uniqueSuburbs = suburbs.filter(
        (suburb, index, self) =>
          index === self.findIndex((s) => s.suburb === suburb.suburb)
      );

      return { suburbs: uniqueSuburbs, token };
    } catch (err) {
      return rejectWithValue(
        (err as any).response?.data || "Something went wrong"
      );
    }
  }
);

/**
 * GET method
 * getJobDetailsBasedOnAddressThunk - Get the job details around the candidate address
 */
export const getJobDetailsBasedOnAddressThunk = createAsyncThunk<
  TGetJobDetailsBasedOnAddressResponse, 
  string,
  { rejectValue: any }
>(
  "auth/getJobDetailsBasedOnAddressThunk",
  async (data, { rejectWithValue }) => {
    try {
      const responseJobDetails: AxiosResponse<TGetJobDetailsBasedOnAddressResponse> = await axios.get(
        `/v1/nearest-centres?address=${data}`
      );
      return responseJobDetails.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data);
    }
  }
);